'use client';
import Link from 'next/link';
import { ReactElement, useId, useState, useRef, useEffect } from 'react';
import CaretIcon from 'components/icons/CaretIcon';

export default function LearnDropdownButton({
  toggleMenu,
  pathname,
}: {
  toggleMenu: () => void;
  pathname: string;
}): ReactElement | null {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownMenuId = useId();
  const isCurrentPage = (page: string, path: string) => page === path;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleMenuItemClick = () => {
    setIsOpen(false);
    toggleMenu();
  };

  const LEARN_MENU_ITEMS = [
    { href: '/medicare-university', label: 'Medicare University' },
    { href: '/resources', label: 'Resources' },
    {
      href: 'https://blog.myalexhealth.com/medicare-made-simple',
      label: 'MyALEXHealth Blog',
      external: true,
    },
  ];

  return (
    <div className="learn-dropdown" ref={dropdownRef}>
      <button
        data-testid="learn-dropdown"
        onClick={toggleDropdown}
        className={`menu-button${isOpen ? ' active' : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls={dropdownMenuId}
      >
        <span>Learn</span>
        <CaretIcon direction={isOpen ? 'up' : 'down'} size="large" />
      </button>

      {isOpen && (
        <div id={dropdownMenuId} className="dropdown-menu">
          {LEARN_MENU_ITEMS.map(({ href, label, external }) => {
            const isCurrent = !external && isCurrentPage(href, pathname);
            return (
              <Link
                key={href}
                href={href}
                onClick={handleMenuItemClick}
                className={isCurrent ? 'current-page' : ''}
                aria-current={isCurrent ? 'page' : undefined}
              >
                {label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
